import {
    MAINTENANCE,
    NETWORK_ERROR,
    NETWORK_ERROR_MSG,
    OTHER,
    SERVER_ERROR,
    SERVER_ERROR_MSG,
    SERVICE_UNAVAILABLE_MSG,
    SERVICE_UNAVAILABLE_STATUS_CODE,
    UNKNOWN_ERROR_MSG,
} from '@constants/api.ts';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { extractError } from '@utils/slice.ts';
import type { AxiosError } from 'axios';

import type { HealthInfoResponse } from '../api/health.api.ts';
import { getHealthInfo } from '../api/health.api.ts';

interface HealthSlice {
    isLoading: boolean;
    error: Record<string, any> | null;
    healthStatus: HealthInfoResponse | null;
}

const initialState: HealthSlice = {
    isLoading: false,
    error: null,
    healthStatus: null,
};

export const getHealthStatus = createAsyncThunk<HealthInfoResponse, void, { rejectValue: any }>(
    'health/healthStatus',
    (_, { rejectWithValue }) =>
        getHealthInfo()
            .then((res) => res)
            .catch((error) => {
                const axiosError = error as AxiosError;
                const message = axiosError?.message;

                if (!axiosError?.response) {
                    return rejectWithValue({
                        type: NETWORK_ERROR,
                        message: message || NETWORK_ERROR_MSG,
                    });
                }

                const status = axiosError?.response?.status;

                if (status === SERVICE_UNAVAILABLE_STATUS_CODE) {
                    return rejectWithValue({
                        type: MAINTENANCE,
                        status,
                        message: message || SERVICE_UNAVAILABLE_MSG,
                    });
                }

                if (status >= 500 && status < 600) {
                    return rejectWithValue({
                        type: SERVER_ERROR,
                        status,
                        message: message || SERVER_ERROR_MSG,
                    });
                }

                return rejectWithValue({
                    type: OTHER,
                    status,
                    message: message || UNKNOWN_ERROR_MSG,
                });
            }),
);

const healthSlice = createSlice({
    name: 'health',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(getHealthStatus.fulfilled, (state, action) => {
            state.healthStatus = action.payload;
            state.isLoading = false;
        });
        builder.addCase(getHealthStatus.pending, (state) => {
            state.error = null;
            state.isLoading = true;
        });
        builder.addCase(getHealthStatus.rejected, (state, action) => {
            state.isLoading = false;
            state.error = extractError(action);
        });
    },
});

export default healthSlice.reducer;
